import { createSlice } from "@reduxjs/toolkit";

const initialState = {
  brandName: "",
  registrationNumber: "",
  country: "",
  state: "",
  postalCode: "",
  categories: [],
  tellUsMore: "",
  impactoverseBenefits: "",
  profilePicture: null, // Blob URL for preview
  coverPicture: null, // Blob URL for preview
  otherMedia: null, // Blob URL for preview
  profilePictureFile: null, // Actual File object
  coverPictureFile: null, // Actual File object
  otherMediaFile: null, // Actual File object
  primaryContact: {
    name: "",
    email: "",
    phone: "",
  },
  secondaryContact: {
    name: "",
    email: "",
    phone: "",
  },
  socialMediaLinks: {
    instagram: "",
    youtube: "",
    facebook: "",
    linkedin: "",
  },
  // Step completion tracking
  stepsCompleted: {
    step1: false,
    step2: false,
    step3: false,
    step4: false,
    step5: false,
  },
};

export const brandVerificationSlice = createSlice({
  name: "brandVerification",
  initialState,
  reducers: {
    updateBrandVerification: (state, action) => {
      return { ...state, ...action.payload };
    },
    resetBrandVerification: () => initialState,
    markStepComplete: (state, action) => {
      const stepNumber = action.payload;
      state.stepsCompleted[`step${stepNumber}`] = true;
    },
    markStepIncomplete: (state, action) => {
      const stepNumber = action.payload;
      state.stepsCompleted[`step${stepNumber}`] = false;
    },
  },
});

// Validation functions for each step
export const validateStep1 = (state) => {
  return !!(
    state.brandName?.trim() &&
    state.registrationNumber?.trim() &&
    state.country?.trim() &&
    state.state?.trim() &&
    state.postalCode?.trim() &&
    state.categories?.length > 0
  );
};

export const validateStep4 = (state) => {
  const primary = state.primaryContact;
  const secondary = state.secondaryContact;
  
  const emailPattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  const phonePattern = /^[\+]?[1-9][\d]{0,15}$/;
  
  return !!(
    primary?.name?.trim() &&
    primary?.email?.trim() &&
    emailPattern.test(primary.email) &&
    primary?.phone?.trim() &&
    phonePattern.test(primary.phone) &&
    secondary?.name?.trim() &&
    secondary?.email?.trim() &&
    emailPattern.test(secondary.email) &&
    secondary?.phone?.trim() &&
    phonePattern.test(secondary.phone)
  );
};

// Names the fields the backend requires but the collected data is missing, so
// the review step can say what is wrong instead of naming a step number.
export const getMissingBrandFields = (state) => {
  const emailPattern = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
  const phonePattern = /^[\+]?[1-9][\d]{0,15}$/;
  const missing = [];

  if (!state.brandName?.trim()) missing.push("Brand name");
  if (!state.registrationNumber?.trim()) missing.push("Registration number");
  if (!state.country?.trim()) missing.push("Country of operation");
  if (!state.state?.trim()) missing.push("State/Region");
  if (!state.postalCode?.trim()) missing.push("Postal code");
  if (!(state.categories?.length > 0)) missing.push("At least one category");

  [
    ["Primary", state.primaryContact],
    ["Secondary", state.secondaryContact],
  ].forEach(([label, contact]) => {
    if (!contact?.name?.trim()) missing.push(`${label} contact name`);
    if (!contact?.email?.trim() || !emailPattern.test(contact.email.trim()))
      missing.push(`${label} contact email`);
    if (!contact?.phone?.trim() || !phonePattern.test(contact.phone.trim()))
      missing.push(`${label} contact phone number`);
  });

  return missing;
};

// Helper to check if user can access a given step
export const canAccessStep = (state, targetStep) => {
  if (targetStep === 1) return true;
  for (let i = 1; i < targetStep; i++) {
    if (!state.stepsCompleted[`step${i}`]) {
      return false;
    }
  }
  return true;
};

// Helper to get first incomplete step
export const getFirstIncompleteStep = (state) => {
  for (let i = 1; i <= 5; i++) {
    if (!state.stepsCompleted[`step${i}`]) {
      return i;
    }
  }
  return 6; // All steps complete, can go to review
};

export const { updateBrandVerification, resetBrandVerification, markStepComplete, markStepIncomplete } =
  brandVerificationSlice.actions;
export default brandVerificationSlice.reducer;
